import type { GlobalDividendCalendarEvent } from '../services/globalDividendCalendarService';
import type { ProjectedDividendEvent } from '../services/dividendProjectionService';

export type UserDividendHoldingInput = {
    symbol: string;
    name?: string;
    quantity: number;
    currency?: string | null;
    baseCurrency?: string | null;
    aliases?: string[];
};

export type BuildUserDividendForecastArgs = {
    holdings: UserDividendHoldingInput[];
    globalEvents: GlobalDividendCalendarEvent[];
    projectedEvents?: ProjectedDividendEvent[];
    selectedCurrency: string;
    horizonMonths?: number;
    now?: Date;
    fxRates?: Record<string, number>;
};

export type UserDividendForecastEvent = {
    id: string;
    symbol: string;
    name: string;
    exDate?: string | null;
    paymentDate?: string | null;
    eventDate: string;
    dateType: 'PAYMENT_DATE' | 'EX_DATE';
    amountPerShare: number | null;
    quantity: number;
    expectedCashNative: number | null;
    nativeCurrency: string | null;
    expectedCashBase: number | null;
    baseCurrency: string;
    frequency?: string | null;
    declared: boolean;
    estimated?: boolean;
    confidence?: 'low' | 'medium' | 'high' | string | null;
    source: 'FMP_CALENDAR' | string;
};

export type UserDividendForecastMonth = {
    monthKey: string;
    label: string;
    amount: number;
    events: UserDividendForecastEvent[];
};

export type UserDividendForecastResult = {
    upcomingEvents: UserDividendForecastEvent[];
    monthlyForecast: UserDividendForecastMonth[];
    next12mTotal: number;
    monthlyAverage: number;
    confirmedForwardAnnual: number;
    missingCurrencyEvents: UserDividendForecastEvent[];
};

type AggregatedHolding = {
    symbol: string;
    name: string;
    quantity: number;
    aliases: string[];
};

const DEFAULT_HORIZON_MONTHS = 12;
const LOOKBACK_DAYS = 5;
const STABLE_MONTH_COUNT = 12;
const PROJECTION_DUPLICATE_WINDOW_DAYS = 10;

const normalizeSymbol = (symbol: unknown) => String(symbol || '').trim().toUpperCase();
const normalizeCurrency = (currency: unknown) => {
    const normalized = String(currency || '').trim().toUpperCase();
    return normalized || null;
};

const startOfUtcDay = (date: Date) => new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
const toIsoDay = (date: Date) => date.toISOString().slice(0, 10);
const monthKeyForDate = (isoDate: string) => isoDate.slice(0, 7);

const addDaysUtc = (date: Date, days: number) => {
    const next = new Date(date);
    next.setUTCDate(next.getUTCDate() + days);
    return next;
};

const addMonthsUtc = (date: Date, months: number) => {
    const next = new Date(date);
    next.setUTCMonth(next.getUTCMonth() + months);
    return next;
};

const normalizeDateString = (value: unknown) => {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    const isoDay = trimmed.slice(0, 10);
    const parsed = new Date(`${isoDay}T00:00:00Z`);
    return Number.isFinite(parsed.getTime()) ? isoDay : null;
};

const getEventDate = (event: Pick<GlobalDividendCalendarEvent, 'paymentDate' | 'exDate'>) => {
    const paymentDate = normalizeDateString(event.paymentDate);
    if (paymentDate) return { eventDate: paymentDate, dateType: 'PAYMENT_DATE' as const };

    const exDate = normalizeDateString(event.exDate);
    if (exDate) return { eventDate: exDate, dateType: 'EX_DATE' as const };

    return null;
};

const monthLabel = (monthStart: Date) => monthStart.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });

const aggregateHoldings = (holdings: UserDividendHoldingInput[]) => {
    const holdingsBySymbol = new Map<string, AggregatedHolding>();

    holdings.forEach((holding) => {
        const symbol = normalizeSymbol(holding.symbol);
        if (!symbol) return;

        const quantity = Number(holding.quantity || 0);
        const aliases = (holding.aliases || []).map(normalizeSymbol).filter(Boolean);
        const existing = holdingsBySymbol.get(symbol);

        if (existing) {
            existing.quantity += quantity;
            existing.aliases = Array.from(new Set([...existing.aliases, ...aliases]));
            if (!existing.name && holding.name) existing.name = holding.name;
            return;
        }

        holdingsBySymbol.set(symbol, {
            symbol,
            name: holding.name || symbol,
            quantity,
            aliases,
        });
    });

    const holdingsByMatchSymbol = new Map<string, AggregatedHolding>();
    holdingsBySymbol.forEach((holding) => {
        [holding.symbol, ...holding.aliases].forEach((symbol) => {
            if (symbol && !holdingsByMatchSymbol.has(symbol)) {
                holdingsByMatchSymbol.set(symbol, holding);
            }
        });
    });

    return holdingsByMatchSymbol;
};

const dedupeGlobalEvents = (globalEvents: GlobalDividendCalendarEvent[]) => {
    const eventsByKey = new Map<string, GlobalDividendCalendarEvent>();

    globalEvents.forEach((event) => {
        const dateInfo = getEventDate(event);
        const eventDate = dateInfo?.eventDate || '';
        const eventId = typeof event.eventId === 'string' ? event.eventId.trim() : '';
        const key = eventId
            ? `id:${eventId}`
            : `fallback:${normalizeSymbol(event.symbol)}:${eventDate}:${String(event.amountPerShare ?? '')}:${normalizeCurrency(event.currency) || ''}`;

        if (!eventsByKey.has(key)) {
            eventsByKey.set(key, event);
        }
    });

    return Array.from(eventsByKey.values());
};

const resolveExpectedCashBase = ({
    expectedCashNative,
    nativeCurrency,
    selectedCurrency,
    fxRates,
}: {
    expectedCashNative: number;
    nativeCurrency: string | null;
    selectedCurrency: string;
    fxRates: Record<string, number>;
}) => {
    if (nativeCurrency === selectedCurrency) return expectedCashNative;
    if (!nativeCurrency) return null;

    const compactRate = fxRates[`${nativeCurrency}${selectedCurrency}`];
    if (Number.isFinite(compactRate)) return expectedCashNative * compactRate;

    const underscoredRate = fxRates[`${nativeCurrency}_${selectedCurrency}`];
    if (Number.isFinite(underscoredRate)) return expectedCashNative * underscoredRate;

    return null;
};

const buildStableMonthlyForecast = (now: Date, events: UserDividendForecastEvent[]) => {
    const monthStarts = Array.from({ length: STABLE_MONTH_COUNT }, (_, index) => {
        const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
        return addMonthsUtc(start, index);
    });

    const eventsByMonth = new Map<string, UserDividendForecastEvent[]>();
    events
        .filter((event) => event.expectedCashBase !== null)
        .forEach((event) => {
            const monthKey = monthKeyForDate(event.eventDate);
            const monthEvents = eventsByMonth.get(monthKey) || [];
            monthEvents.push(event);
            eventsByMonth.set(monthKey, monthEvents);
        });

    return monthStarts.map((monthStart) => {
        const monthKey = monthStart.toISOString().slice(0, 7);
        const monthEvents = (eventsByMonth.get(monthKey) || []).sort(sortForecastEvents);
        const amount = monthEvents.reduce((sum, event) => sum + (event.expectedCashBase ?? 0), 0);

        return {
            monthKey,
            label: monthLabel(monthStart),
            amount,
            events: monthEvents,
        };
    });
};

const sortForecastEvents = (left: UserDividendForecastEvent, right: UserDividendForecastEvent) => (
    left.eventDate.localeCompare(right.eventDate) || left.symbol.localeCompare(right.symbol)
);

export const buildUserDividendForecast = ({
    holdings,
    globalEvents,
    selectedCurrency,
    horizonMonths = DEFAULT_HORIZON_MONTHS,
    now = new Date(),
    fxRates = {},
    projectedEvents = [],
}: BuildUserDividendForecastArgs): UserDividendForecastResult => {
    const baseCurrency = normalizeCurrency(selectedCurrency) || selectedCurrency;
    const today = startOfUtcDay(now);
    const fromDate = addDaysUtc(today, -LOOKBACK_DAYS);
    const toDate = addMonthsUtc(today, horizonMonths);
    const holdingsByMatchSymbol = aggregateHoldings(holdings);
    const upcomingEvents: UserDividendForecastEvent[] = [];
    const missingCurrencyEvents: UserDividendForecastEvent[] = [];


    const confirmedBySymbol = new Map<string, string[]>();

    dedupeGlobalEvents(globalEvents).forEach((globalEvent) => {
        const eventSymbol = normalizeSymbol(globalEvent.symbol);
        const holding = holdingsByMatchSymbol.get(eventSymbol);
        if (!holding) return;

        const dateInfo = getEventDate(globalEvent);
        if (!dateInfo) return;

        const eventDateObject = new Date(`${dateInfo.eventDate}T00:00:00Z`);
        if (eventDateObject < fromDate || eventDateObject > toDate) return;

        const amount = Number(globalEvent.amountPerShare);
        const amountPerShare = Number.isFinite(amount) && amount > 0 ? amount : null;
        const nativeCurrency = normalizeCurrency(globalEvent.currency);
        const expectedCashNative = amountPerShare === null ? null : amountPerShare * holding.quantity;
        const expectedCashBase = expectedCashNative === null
            ? null
            : resolveExpectedCashBase({ expectedCashNative, nativeCurrency, selectedCurrency: baseCurrency, fxRates });

        const event: UserDividendForecastEvent = {
            id: (typeof globalEvent.eventId === 'string' && globalEvent.eventId.trim())
                || `${eventSymbol}-${dateInfo.eventDate}-${String(globalEvent.amountPerShare ?? 'TBA')}-${nativeCurrency || 'UNKNOWN'}`,
            symbol: holding.symbol,
            name: holding.name,
            exDate: normalizeDateString(globalEvent.exDate),
            paymentDate: normalizeDateString(globalEvent.paymentDate),
            eventDate: dateInfo.eventDate,
            dateType: dateInfo.dateType,
            amountPerShare,
            quantity: holding.quantity,
            expectedCashNative,
            nativeCurrency,
            expectedCashBase,
            baseCurrency,
            frequency: globalEvent.frequency ?? null,
            declared: true,
            source: globalEvent.source || 'FMP_CALENDAR',
        };

        upcomingEvents.push(event);
        const confirmedDates = confirmedBySymbol.get(holding.symbol) || [];
        confirmedDates.push(event.eventDate);
        confirmedBySymbol.set(holding.symbol, confirmedDates);
        if (amountPerShare !== null && expectedCashBase === null) {
            missingCurrencyEvents.push(event);
        }
    });


    projectedEvents.forEach((projectedEvent) => {
        const eventSymbol = normalizeSymbol(projectedEvent.symbol);
        const holding = holdingsByMatchSymbol.get(eventSymbol);
        if (!holding) return;

        const dateInfo = getEventDate(projectedEvent);
        if (!dateInfo) return;

        const eventDateObject = new Date(`${dateInfo.eventDate}T00:00:00Z`);
        if (eventDateObject < fromDate || eventDateObject > toDate) return;

        const hasNearbyConfirmed = (confirmedBySymbol.get(holding.symbol) || []).some((confirmedDate) => {
            const confirmed = new Date(`${confirmedDate}T00:00:00Z`);
            const diffDays = Math.abs((eventDateObject.getTime() - confirmed.getTime()) / (1000 * 60 * 60 * 24));
            return diffDays <= PROJECTION_DUPLICATE_WINDOW_DAYS;
        });
        if (hasNearbyConfirmed) return;

        const amount = Number(projectedEvent.amountPerShare);
        const amountPerShare = Number.isFinite(amount) && amount > 0 ? amount : null;
        const nativeCurrency = normalizeCurrency(projectedEvent.currency);
        const expectedCashNative = amountPerShare === null ? null : amountPerShare * holding.quantity;
        const expectedCashBase = expectedCashNative === null
            ? null
            : resolveExpectedCashBase({ expectedCashNative, nativeCurrency, selectedCurrency: baseCurrency, fxRates });

        upcomingEvents.push({
            id: projectedEvent.eventId || `${holding.symbol}-${dateInfo.eventDate}-PROJECTED`,
            symbol: holding.symbol,
            name: holding.name,
            exDate: normalizeDateString(projectedEvent.exDate),
            paymentDate: normalizeDateString(projectedEvent.paymentDate),
            eventDate: dateInfo.eventDate,
            dateType: dateInfo.dateType,
            amountPerShare,
            quantity: holding.quantity,
            expectedCashNative,
            nativeCurrency,
            expectedCashBase,
            baseCurrency,
            frequency: projectedEvent.frequency ?? null,
            declared: false,
            estimated: true,
            confidence: projectedEvent.confidence ?? 'low',
            source: projectedEvent.source || 'PROJECTED_HISTORY',
        });
    });

    upcomingEvents.sort(sortForecastEvents);
    missingCurrencyEvents.sort(sortForecastEvents);

    const next12mTotal = upcomingEvents.reduce((sum, event) => sum + (event.expectedCashBase ?? 0), 0);

    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
        console.debug('[dividend-forecast] diagnostics', {
            holdingsSymbols: holdings.map((h) => normalizeSymbol(h.symbol)).filter(Boolean),
            globalEventsCount: globalEvents.length,
            matchedConfirmedEventsCount: confirmedBySymbol.size,
            missingCurrencyEventsCount: missingCurrencyEvents.length,
            sampleMissingCurrencyEvents: missingCurrencyEvents.slice(0, 10).map((event) => ({ symbol: event.symbol, exDate: event.exDate, paymentDate: event.paymentDate, amountPerShare: event.amountPerShare, nativeCurrency: event.nativeCurrency })),
            upcomingEventsCount: upcomingEvents.length,
        });
    }
    const monthlyForecast = buildStableMonthlyForecast(today, upcomingEvents);

    return {
        upcomingEvents,
        monthlyForecast,
        next12mTotal,
        monthlyAverage: next12mTotal / STABLE_MONTH_COUNT,
        confirmedForwardAnnual: next12mTotal,
        missingCurrencyEvents,
    };
};
